import { userCanMessageNonMembers } from '../../backend/features/permissions/shared';
import { showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { groupIdPrefixForAlertService } from '@juvare/contact-management/backend/features/groups/shared';
import { orgIdPrefixForAlertService } from '@juvare/contact-management/backend/features/organizations/shared';
import { allowUserSendMessages } from '@juvare/contact-management/backend/features/permissions/shared';
import SendIcon from '@mui/icons-material/Send';
import React from 'react';
import { showNotificationDialog } from '../features/alerts';
import { useAlertUiTranslation } from './useAlertUiTranslation';
import { useUserState } from './useUserState';
// IMPORTANT these hooks must not use anything that requires ContactManagement context to be present
export function useContactManagementCustomization() {
    const { t } = useAlertUiTranslation();
    const userState = useUserState();
    const accountsTableCustomization = useAccountsTableCustomization();
    const notificationsConfig = {};
    if (allowUserSendMessages(userState)) {
        notificationsConfig.onAccountNotificationSend = (accountIds) => {
            showNotificationDialogWithRecipients(t, {
                includeNonMembers: userCanMessageNonMembers(userState),
                accounts: accountIds,
                organizations: [],
                groups: [],
            });
        };
    }
    if (allowUserSendMessages(userState)) {
        notificationsConfig.onGroupNotificationSend = (groupId) => {
            const groupIdWithoutPrefix = groupId.startsWith(groupIdPrefixForAlertService)
                ? groupId.slice(groupIdPrefixForAlertService.length)
                : groupId;
            showNotificationDialogWithRecipients(t, {
                includeNonMembers: userCanMessageNonMembers(userState),
                accounts: [],
                organizations: [],
                groups: [groupIdWithoutPrefix],
            });
        };
    }
    if (allowUserSendMessages(userState)) {
        notificationsConfig.onOrganizationNotificationSend = (organizationId, accountIds) => {
            const orgIdWithoutPrefix = organizationId.startsWith(orgIdPrefixForAlertService)
                ? organizationId.slice(orgIdPrefixForAlertService.length)
                : organizationId;
            showNotificationDialogWithRecipients(t, {
                includeNonMembers: userCanMessageNonMembers(userState),
                accounts: accountIds ? accountIds : [],
                organizations: accountIds ? [] : [orgIdWithoutPrefix],
                groups: [],
            });
        };
    }
    return {
        notificationConfig: notificationsConfig,
        accountsTableCustomization: accountsTableCustomization,
    };
}
export function useAccountsTableCustomization() {
    const userState = useUserState();
    const { t } = useAlertUiTranslation();
    const handleNotifyAll = (tableLocation) => {
        if ((tableLocation === null || tableLocation === void 0 ? void 0 : tableLocation.name) === 'org-members') {
            showNotificationDialogWithRecipients(t, {
                includeNonMembers: false,
                accounts: [],
                organizations: [tableLocation.organizationId],
                groups: [],
            });
        }
        else if ((tableLocation === null || tableLocation === void 0 ? void 0 : tableLocation.name) === 'group-members') {
            showNotificationDialogWithRecipients(t, {
                includeNonMembers: false,
                accounts: [],
                organizations: [],
                groups: [tableLocation.groupId],
            });
        }
        else {
            showNotificationDialogWithRecipients(t, {
                includeNonMembers: false,
                accounts: [],
                accountsFilter: [],
                organizations: [],
                groups: [],
            });
        }
    };
    const handleAccountNotificationSendByIds = (accountIds) => {
        showNotificationDialogWithRecipients(t, {
            includeNonMembers: userCanMessageNonMembers(userState),
            accounts: accountIds,
            organizations: [],
            groups: [],
        });
    };
    const handleAccountNotificationSendByFilter = (appliedStorageFilter) => {
        showNotificationDialogWithRecipients(t, {
            includeNonMembers: userCanMessageNonMembers(userState),
            accounts: [],
            // remove any systemStatus filter, because systemStatus = 'active' filter is always applied when sending alerts
            accountsFilter: appliedStorageFilter.filter((f) => !('column' in f && f.column === 'systemStatus')),
            organizations: [],
            groups: [],
        });
    };
    return {
        getDataTableProps: (dataTableProps, appliedFilterData, tableLocation) => {
            var _a, _b;
            const globalActions = [...((_a = dataTableProps.globalActions) !== null && _a !== void 0 ? _a : [])];
            const localActions = [...((_b = dataTableProps.localActions) !== null && _b !== void 0 ? _b : [])];
            if (allowUserSendMessages(userState)) {
                // TODO this code tries to keep one action per render cycle, CM allows to mutate datatable prop,
                // TODO if we would have other libs adding to array, we try to keep Alert actions stable by adding or replacing our action within array
                const globalActionIdx = globalActions.findIndex((a) => a['data-test'] === 'notify-global-action-button');
                if (globalActionIdx !== -1) {
                    globalActions.splice(globalActionIdx, 1);
                }
                globalActions.push({
                    icon: React.createElement(SendIcon, null),
                    label: t('common.notifyAll'),
                    onClick: () => {
                        handleNotifyAll(tableLocation);
                    },
                    'data-test': 'notify-global-action-button',
                });
                const localActionIdx = localActions.findIndex((a) => a['data-test'] === 'notify-local-action-button');
                if (localActionIdx !== -1) {
                    localActions.splice(localActionIdx, 1);
                }
                localActions.push({
                    icon: React.createElement(SendIcon, null),
                    label: t('common.notify'),
                    onClick: () => {
                        var _a, _b, _c, _d;
                        if ((_a = dataTableProps.selectedRowIds) === null || _a === void 0 ? void 0 : _a.length) {
                            handleAccountNotificationSendByIds(dataTableProps.selectedRowIds);
                        }
                        else {
                            handleAccountNotificationSendByFilter((_d = (_c = (_b = dataTableProps.customBackendResponseData) === null || _b === void 0 ? void 0 : _b.storageClientRequest) === null || _c === void 0 ? void 0 : _c.filter) !== null && _d !== void 0 ? _d : []);
                        }
                    },
                    'data-test': 'notify-local-action-button',
                });
            }
            return Object.assign(Object.assign({}, dataTableProps), { globalActions,
                localActions });
        },
    };
}
function showNotificationDialogWithRecipients(t, internalRecipients) {
    showNotificationDialog({
        mode: 'notification',
        reviewFormHeader: t('pages.newFromTemplate.reviewAndSend'),
        wizardHeader: t('pages.newFromTemplate.createNotification'),
        onSubmitSuccess: (_data, onClose) => {
            showAlertSnackbar({
                message: t('pages.edit.snackbar.notification.sent'),
                type: 'success',
                autoHideDuration: 1000,
            });
            onClose();
        },
        partialAlert: { recipients: { internal: internalRecipients } },
    });
}
